import * as React from 'react';
import propz from 'propz';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { Button } from 'Src/components/Button/Button';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { Map } from 'Src/components/Map/Map';
import { AppUser } from '../../../../../App/App';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { resolveAgeNamingFromContext, formatAgesCompact } from 'Src/helpers/ageGroups';
import { Point } from '../../../../../../models/venue';
import { Autocomplete } from '../../../../../../components/Autocomplete/Autocomplete';
import {
  convertPlaceToPostcodeWithPlaceName,
  getDefaultPoint,
  getPostcodes,
  isPlace
} from '../../../../../../helpers/venue/venue';
import { Switch } from '../../../../../../components/Switch/Switch';
import { uploadImage } from '../../../../../../helpers/service/image';
import { getNameByPlaceOrPostcode } from '../../../../../../helpers/autocomplete/autocomplete';
import { isClubEvent } from '../../../../../../helpers/event/event';
import { CLUB_EVENT_EDIT_MODE, EVENT_TYPES } from '../../../../../../consts/event';
import { SchoolEvent } from '../../../../../../models/event';
import { SchoolForm } from '../../../../../../models/form';
import { getAgesForMultiSelect, getAgesFromMultiSelect } from '../../../../../../helpers/multiselect/multiselect';
import { SchoolEventClubEventRadioButtons } from '../SchoolEventClubEventRadioButtons/SchoolEventClubEventRadioButtons';
import './SchoolEventsEditSettingsForm.scss';

const AGE_KEY_STAGES = [
  { label: 'EYFS', names: ['Reception'] },
  { label: 'KS1', names: ['Y1', 'Y2'] },
  { label: 'KS2', names: ['Y3', 'Y4', 'Y5', 'Y6'] },
  { label: 'KS3', names: ['Y7', 'Y8', 'Y9'] },
  { label: 'KS4', names: ['Y10', 'Y11'] },
  { label: 'KS5', names: ['Y12', 'Y13'] }
];

function getAgeKeyStageGroups(ages: any[]) {
  const indexedAges = (ages || []).map((item, index) => ({ item, index }));
  const assigned = new Set<number>();
  const groups = AGE_KEY_STAGES.map(stage => ({
    label: stage.label,
    items: indexedAges.filter(({ item, index }) => {
      const name = String(item.name || '').replace(/\s+/g, '');
      const isMatch = stage.names.some(stageName => stageName.toLowerCase() === name.toLowerCase());
      if (isMatch) assigned.add(index);
      return isMatch;
    })
  })).filter(group => group.items.length > 0);
  const otherItems = indexedAges.filter(({ index }) => !assigned.has(index));
  return otherItems.length ? [...groups, { label: 'Other', items: otherItems }] : groups;
}

interface Props {
  user: AppUser;
  schoolEvent: SchoolEvent;
  forms: SchoolForm[];
  onCancel: () => void;
  onSubmit: (data: any, editMode: string, reasonForChange: string) => void;
}

interface State {
  point: Point;
  venue: any;
  isDeleteVenueModalOpen: boolean;
  isCreateVenueFormOpen: boolean;
  isImageError: boolean;
  isChangeFollowingEvents: boolean;
  editMode: string;
  reasonForChange: string;
  isReasonForChangeVisible: boolean;
  isHelpModalOpen: boolean;
  ageSearch: string;
  isAgeDropdownOpen: boolean;
  isChallenge: boolean;
  isMapExpanded: boolean;
  activeTab: 'details' | 'venue';
}

export class SchoolEventsEditSettingsForm extends React.Component<Props, State> {
  private ageDropdownRef = React.createRef<HTMLDivElement>();
  constructor(props) {
    super(props);

    const { schoolEvent, user } = this.props;
    const initialPicUrl = propz.get(schoolEvent, ['photos', '0', 'picUrl'], '');
    const initialLeaguePositionsCount = Number(propz.get(schoolEvent, ['leaguePositionsCount'], 0));
    const initialIsStudentCanSubmitResult = propz.get(schoolEvent, ['isStudentCanSubmitResult'], false);
    const initialIsStudentCanJoin = propz.get(schoolEvent, ['isStudentCanJoin'], false);

    const isChallengeInitially =
      !!initialPicUrl || initialLeaguePositionsCount > 0 || initialIsStudentCanSubmitResult || initialIsStudentCanJoin;
    const venue = propz.get(schoolEvent, ['venue']);
    const convertPlace = typeof schoolEvent !== 'undefined' ? convertPlaceToPostcodeWithPlaceName(venue) : undefined;

    this.state = {
      point: propz.get(convertPlace, ['point'], getDefaultPoint(user)),
      venue: schoolEvent.venue,
      isDeleteVenueModalOpen: false,
      isCreateVenueFormOpen: false,
      isImageError: false,
      isChangeFollowingEvents: false,
      editMode: CLUB_EVENT_EDIT_MODE.SINGLE,
      reasonForChange: '',
      isReasonForChangeVisible: false,
      isHelpModalOpen: false,
      ageSearch: '',
      isAgeDropdownOpen: false,
      isChallenge: isChallengeInitially,
      isMapExpanded: false,
      activeTab: 'details'
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  onDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target as Node | null;

    if (this.state.isAgeDropdownOpen) {
      const node = this.ageDropdownRef.current;
      if (node && target && !node.contains(target)) {
        this.closeAgeDropdown();
      }
    }
  };

  onAgesChange = (index, setFieldValue, values) => {
    const ageItems = values.ages || [];
    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    setFieldValue('ages', ageItemsUpdated);
  };

  getSelectedAgesLabel = values => {
    const { user, schoolEvent } = this.props;
    const naming = resolveAgeNamingFromContext(user, schoolEvent);

    const selectedAges = (values.ages || [])
      .filter(item => item.isChecked)
      .map(item => Number(propz.get(item, ['age'], item.key)))
      .filter(age => Number.isFinite(age));

    if (selectedAges.length === 0) return 'Select ages';

    return formatAgesCompact(selectedAges, naming);
  };

  toggleAgeDropdown = () => {
    this.setState(prev => ({
      isAgeDropdownOpen: !prev.isAgeDropdownOpen
    }));
  };

  closeAgeDropdown = () => {
    this.setState({
      isAgeDropdownOpen: false,
      ageSearch: ''
    });
  };

  onAgeSearchChange = event => {
    this.setState({
      ageSearch: event.target.value
    });
  };

  onSelectAllAges = (setFieldValue, values) => {
    const next = (values.ages || []).map(item => ({
      ...item,
      isChecked: true
    }));

    setFieldValue('ages', next);
  };

  onClearAllAges = (setFieldValue, values) => {
    const next = (values.ages || []).map(item => ({
      ...item,
      isChecked: false
    }));

    setFieldValue('ages', next);
  };

  onSelectFilteredAges = (setFieldValue, values) => {
    const query = String(this.state.ageSearch || '')
      .trim()
      .toLowerCase();

    const next = (values.ages || []).map(item => {
      const matches =
        query === '' ||
        String(item.name || '')
          .toLowerCase()
          .includes(query);

      return matches ? { ...item, isChecked: true } : item;
    });

    setFieldValue('ages', next);
  };

  onSubmitVenueForm = (data): void => {
    const { user } = this.props;

    createVenue(user, data).then(venue => {
      const convertPlace = convertPlaceToPostcodeWithPlaceName(venue);

      this.setState({
        venue: convertPlace,
        point: convertPlace.point,
        isCreateVenueFormOpen: false
      });
    });
  };

  handleChallengeSwitchChange = event => {
    this.setState({
      isChallenge: event.target.checked
    });
  };

  onImageSelected = (event, setFieldValue) => {
    const image = propz.get(event, ['target', 'files', 0]);
    const { user } = this.props;

    if (!image) {
      return;
    }

    uploadImage(user, image)
      .then(response => {
        const picUrl = `${window.apiImg}/images/${response.key}`;
        setFieldValue('picUrl', picUrl);

        this.setState({
          isImageError: false
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isImageError: true });
      });
  };

  onRemoveImageClick = (setFieldValue): void => {
    setFieldValue('picUrl', '');
  };

  getNewPoint = pointNew => {
    this.setState({
      point: pointNew
    });
  };

  onCancelCreateVenueFormOpenClick = (): void => {
    this.setState({
      isCreateVenueFormOpen: false
    });
  };

  createVenueFormOpen = (): void => {
    this.setState({
      isCreateVenueFormOpen: true
    });
  };

  onIsChangeFollowingEventsClick = () => {
    this.setState({
      isChangeFollowingEvents: true
    });
  };

  onEditModeClick = editMode => {
    this.setState({
      editMode: editMode
    });
  };

  isVenueChanged = nextVenue => {
    const { schoolEvent } = this.props;
    return JSON.stringify(nextVenue) !== JSON.stringify(propz.get(schoolEvent, ['venue']));
  };

  updateReasonVisibility = (values?: { startTime?: any; endTime?: any; venue?: any }) => {
    const { schoolEvent } = this.props;

    const nextStart = propz.get(values, ['startTime']);
    const nextEnd = propz.get(values, ['endTime']);
    const nextVenue = propz.get(values, ['venue']);

    const hasStartTimeChanged =
      typeof nextStart !== 'undefined' &&
      moment(nextStart).toISOString() !== moment(schoolEvent.startTime).toISOString();

    const hasEndTimeChanged =
      typeof nextEnd !== 'undefined' && moment(nextEnd).toISOString() !== moment(schoolEvent.endTime).toISOString();

    const hasVenueChanged = typeof nextVenue !== 'undefined' && this.isVenueChanged(nextVenue);

    this.setState({
      isReasonForChangeVisible: hasStartTimeChanged || hasEndTimeChanged || hasVenueChanged
    });
  };

  handleFieldChange = (fieldName, setFieldValue, value, values) => {
    setFieldValue(fieldName, value);

    if (fieldName === 'startTime') {
      const newEndTime = new Date(value);
      newEndTime.setHours(newEndTime.getHours() + 1);
      setFieldValue('endTime', newEndTime);

      this.updateReasonVisibility({
        ...values,
        startTime: value,
        endTime: newEndTime
      });

      return;
    }

    this.updateReasonVisibility({
      ...values,
      [fieldName]: value
    });
  };

  applyDatePart = (value: Date, datePart: Date): Date => {
    return new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      value.getHours(),
      value.getMinutes(),
      0,
      0
    );
  };

  applyTimePart = (value: Date, timePart: Date): Date => {
    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
      timePart.getHours(),
      timePart.getMinutes(),
      0,
      0
    );
  };

  getDuration = (startTime: Date, endTime: Date): number => {
    const duration = endTime.getTime() - startTime.getTime();
    return duration >= 0 ? duration : 60 * 60 * 1000;
  };

  onStartDateChange = (date: Date, setFieldValue, values) => {
    const duration = this.getDuration(values.startTime, values.endTime);
    const nextStartTime = this.applyDatePart(values.startTime, date);
    const nextEndTime = new Date(nextStartTime.getTime() + duration);

    setFieldValue('startTime', nextStartTime);
    setFieldValue('endTime', nextEndTime);
    this.updateReasonVisibility({ ...values, startTime: nextStartTime, endTime: nextEndTime });
  };

  onStartClockChange = (time: Date, setFieldValue, values) => {
    const nextStartTime = this.applyTimePart(values.startTime, time);
    const nextEndTime = new Date(nextStartTime.getTime() + 60 * 60 * 1000);

    setFieldValue('startTime', nextStartTime);
    setFieldValue('endTime', nextEndTime);
    this.updateReasonVisibility({ ...values, startTime: nextStartTime, endTime: nextEndTime });
  };

  onFinishClockChange = (time: Date, setFieldValue, values) => {
    let nextEndTime = this.applyTimePart(values.startTime, time);

    if (nextEndTime.getTime() < values.startTime.getTime()) {
      nextEndTime = new Date(nextEndTime.getTime() + 24 * 60 * 60 * 1000);
    }

    setFieldValue('endTime', nextEndTime);
    this.updateReasonVisibility({ ...values, endTime: nextEndTime });
  };

  renderCreateVenueForm = () => {
    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={this.state.isCreateVenueFormOpen}
        title="New venue"
        onCloseClick={this.onCancelCreateVenueFormOpenClick}
        customClass="modal-md"
        showFooter={false}
        dialogStyle={{ maxWidth: 680 }}
      >
        <VenueForm user={user} onCancel={this.onCancelCreateVenueFormOpenClick} onSubmit={this.onSubmitVenueForm} />
      </SimpleModal>
    );
  };

  renderHelpModal = () => {
    const { schoolEvent, user } = this.props;
    const isClub = isClubEvent(schoolEvent);

    return (
      <HelpModal
        isOpen={this.state.isHelpModalOpen}
        title="Editing an event"
        badgeText="Event details guide"
        subtitle="What each setting controls, where it is shown, and what happens when you save a change."
        onClose={() => this.setState({ isHelpModalOpen: false })}
      >
        <div className="eSchoolEventEditHelp">
          <div className="eSchoolEventEditHelp__group">
            <h3>Basic details</h3>
            <div className="eSchoolEventEditHelp__item">
              <strong>Date and times</strong>
              <p>
                Set when the event starts and when pupils are expected to finish or be collected. Changing the start
                time initially moves the finish time to one hour later; you can then adjust it.
              </p>
            </div>
            <div className="eSchoolEventEditHelp__item">
              <strong>Short description</strong>
              <p>
                An optional concise event name used in event lists and communications. Leave it blank to keep the
                automatically generated event name.
              </p>
            </div>
            <div className="eSchoolEventEditHelp__item">
              <strong>Age groups</strong>
              <p>
                Select the year groups this event applies to. If none are selected, the event remains available to all
                year groups.
              </p>
            </div>
          </div>

          <div className="eSchoolEventEditHelp__group">
            <h3>Venue &amp; contact</h3>
            <div className="eSchoolEventEditHelp__item">
              <strong>Venue</strong>
              <p>
                Search for an existing venue or create a new one. Open the map only when you need to check or fine-tune
                the exact marker position.
              </p>
            </div>
            <div className="eSchoolEventEditHelp__item">
              <strong>Reply-to email</strong>
              <p>
                Replies to event messages will go to this address. If it is empty, the school’s default contact address
                is used.
              </p>
            </div>
          </div>

          <div className="eSchoolEventEditHelp__notice">
            <strong>When participants may be notified</strong>
            <p>
              Changing the date, time or venue can trigger update notifications. If your school allows a custom update
              message, a “Reason for change” field appears so you can explain what changed.
            </p>
          </div>

          {isClub && (
            <div className="eSchoolEventEditHelp__notice">
              <strong>Recurring club events</strong>
              <p>
                After pressing Save changes, choose whether to update only this occurrence or this and all following
                events in the series.
              </p>
            </div>
          )}
        </div>
      </HelpModal>
    );
  };

  render() {
    const { schoolEvent, forms, onSubmit, onCancel, user } = this.props;
    const { point, isChangeFollowingEvents, editMode, venue, reasonForChange, isReasonForChangeVisible } = this.state;

    const startTimeDateObj = moment(schoolEvent.startTime).toDate();
    const endTimeDateObj = moment(schoolEvent.endTime).toDate();
    const { activeSchool } = user;

    const validationSchema = Yup.object().shape({
      startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
        const { endTime } = this.parent;
        if (!value || !endTime) return true;
        return value <= endTime;
      }),
      endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
        const { startTime } = this.parent;
        if (!value || !startTime) return true;
        return value >= startTime;
      }),
      venue: Yup.mixed().test('venue', 'Required', function() {
        return typeof venue !== 'undefined';
      }),
      replyToEmail: Yup.string()
        .nullable()
        .email('Invalid email format')
    });

    const eventSettingForm = {
      startTime: startTimeDateObj,
      endTime: endTimeDateObj,
      ages: getAgesForMultiSelect(activeSchool, forms, schoolEvent.ages),
      venue: venue,
      picUrl: propz.get(schoolEvent, ['photos', '0', 'picUrl'], ''),
      isStudentCanSubmitResult: schoolEvent.isStudentCanSubmitResult,
      isStudentCanJoin: schoolEvent.isStudentCanJoin,
      leaguePositionsCount: schoolEvent.leaguePositionsCount,
      shortDescription: propz.get(schoolEvent, ['eventDetails', 'shortDescription'], ''),
      replyToEmail: propz.get(schoolEvent, ['replyToEmail'], '')
    };

    const isNonClubEvent = false;
    const showChallengeBlock = false;
    const isChallenge = false;
    const showReasonBlock = isReasonForChangeVisible && user.activeSchool.isEventUpdateNotificationCustomMessageEnabled;

    return (
      <div className="eSchoolEventEditForm">
        {this.renderCreateVenueForm()}
        {this.renderHelpModal()}

        <div className="eSchoolEventEditForm__shell">
          <Formik
            initialValues={eventSettingForm}
            validationSchema={validationSchema}
            onSubmit={values => {
              const {
                ages,
                picUrl,
                replyToEmail,
                leaguePositionsCount,
                isStudentCanSubmitResult,
                isStudentCanJoin,
                ...rest
              } = values;
              const newVenue = { ...venue };

              if (typeof point !== 'undefined') {
                newVenue.point = { ...point };
              }

              const dataToSubmit = {
                ...rest,
                venue: newVenue,
                ages: getAgesFromMultiSelect(activeSchool, forms, ages),
                reasonForChange: this.state.reasonForChange,
                replyToEmail: replyToEmail || ''
              };

              if (isClubEvent(schoolEvent) && !isChangeFollowingEvents) {
                this.onIsChangeFollowingEventsClick();
              } else {
                onSubmit(dataToSubmit, editMode, reasonForChange);
              }
            }}
          >
            {({ values, setFieldValue, touched, errors }) => (
              <Form>
                <div className="eSchoolEventEditForm__tabs" role="tablist" aria-label="Event settings sections">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={this.state.activeTab === 'details'}
                    className={`eSchoolEventEditForm__tab ${this.state.activeTab === 'details' ? 'is-active' : ''}`}
                    onClick={() => this.setState({ activeTab: 'details' })}
                  >
                    Basic details
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={this.state.activeTab === 'venue'}
                    className={`eSchoolEventEditForm__tab ${this.state.activeTab === 'venue' ? 'is-active' : ''}`}
                    onClick={() => this.setState({ activeTab: 'venue' })}
                  >
                    Venue &amp; contact
                  </button>
                </div>

                {this.state.activeTab === 'details' && (
                  <section className="eSchoolEventEditForm__section" role="tabpanel">
                    <div className="eSchoolEventEditForm__sectionTools">
                      <HelpIconButton
                        onClick={() => this.setState({ isHelpModalOpen: true })}
                        label="How event details work"
                      />
                    </div>

                    <div className="eSchoolEventEditForm__timingGrid">
                      <div className="eSchoolEventEditForm__field">
                        <Field name="startTime">
                          {() => (
                            <SitDatePicker
                              centered
                              label="Start date"
                              value={values.startTime}
                              onChange={(date: Date | null) => {
                                if (date) this.onStartDateChange(date, setFieldValue, values);
                              }}
                              format="DD MMM YYYY"
                              required
                              clearable={false}
                              className="eSchoolEventEditForm__datePicker"
                              error={touched.startTime && errors.startTime ? String(errors.startTime) : undefined}
                            />
                          )}
                        </Field>
                      </div>

                      <div className="eSchoolEventEditForm__field">
                        <Field name="startTime">
                          {() => (
                            <TimePicker
                              label="Start time"
                              value={values.startTime}
                              onChange={(time: Date | null) => {
                                if (time) this.onStartClockChange(time, setFieldValue, values);
                              }}
                              minuteStep={5}
                              required
                              showConfirm
                              clearable={false}
                              className="eSchoolEventEditForm__timePicker"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="eSchoolEventEditForm__field">
                        <Field name="endTime">
                          {() => (
                            <TimePicker
                              label="Finish / collection"
                              value={values.endTime}
                              onChange={(time: Date | null) => {
                                if (time) this.onFinishClockChange(time, setFieldValue, values);
                              }}
                              minuteStep={5}
                              required
                              showConfirm
                              clearable={false}
                              className="eSchoolEventEditForm__timePicker"
                              error={touched.endTime && errors.endTime ? String(errors.endTime) : undefined}
                            />
                          )}
                        </Field>
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__grid eSchoolEventEditForm__gridSingle">
                      <div className="eSchoolEventEditForm__field">
                        <label className="eSchoolEventEditForm__label">Short description</label>
                        <Field
                          type="text"
                          name="shortDescription"
                          className="form-control"
                          value={values.shortDescription || ''}
                        />
                        <div className="eSchoolEventEditForm__fieldHint">
                          A concise name shown in event lists and communications.
                        </div>
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__grid eSchoolEventEditForm__gridSingle">
                      <div className="eSchoolEventEditForm__field">
                        <label className="eSchoolEventEditForm__label">Age groups</label>
                        <div className="eSchoolEventEditForm__ageStageList">
                          {getAgeKeyStageGroups(values.ages || []).map(group => (
                            <div key={group.label} className="eSchoolEventEditForm__ageStage">
                              <div className="eSchoolEventEditForm__ageStageLabel">{group.label}</div>
                              <div className="eSchoolEventEditForm__ageChips">
                                {group.items.map(({ item, index }) => (
                                  <button
                                    key={String(item.age || item.name || index)}
                                    type="button"
                                    className={`eSchoolEventEditForm__ageChip ${item.isChecked ? 'is-selected' : ''}`}
                                    onClick={() => this.onAgesChange(index, setFieldValue, values)}
                                  >
                                    {item.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="eSchoolEventEditForm__fieldHint">
                          Leave all off to include every year group.
                        </div>
                        {touched.ages && errors.ages ? (
                          <div className="alert alert-danger mt-2">{String(errors.ages)}</div>
                        ) : null}
                      </div>
                    </div>
                  </section>
                )}

                {this.state.activeTab === 'venue' && (
                  <section className="eSchoolEventEditForm__section" role="tabpanel">
                    <div className="eSchoolEventEditForm__grid eSchoolEventEditForm__gridSingle">
                      <div className="eSchoolEventEditForm__field">
                        <label className="eSchoolEventEditForm__label">Venue</label>

                        <div className="eSchoolEventEditForm__venueRow">
                          <div className="eSchoolEventEditForm__venueInput">
                            <Field name="venue">
                              {() => (
                                <Autocomplete
                                  searchFunction={text => {
                                    const { eventType, invitedSchools, inviterSchool } = schoolEvent;
                                    const isExternalSchoolsType = eventType === EVENT_TYPES.EXTERNAL_SCHOOLS;
                                    const participants = [...invitedSchools];

                                    participants.push(inviterSchool);

                                    return getPostcodes(text, user, isExternalSchoolsType, participants);
                                  }}
                                  getElementTitle={getNameByPlaceOrPostcode}
                                  customClass="mFullWidth mb-0"
                                  placeholder="Start typing the venue name"
                                  defaultItem={this.state.venue}
                                  onSelect={selectedVenue => {
                                    let postcode = selectedVenue;

                                    if (isPlace(postcode)) {
                                      postcode = convertPlaceToPostcodeWithPlaceName(postcode);
                                    }

                                    setFieldValue('venue', postcode);
                                    this.setState({
                                      venue: postcode
                                    });
                                    this.getNewPoint(postcode.point);
                                    this.updateReasonVisibility({
                                      ...values,
                                      venue: postcode
                                    });
                                  }}
                                />
                              )}
                            </Field>
                          </div>

                          <button
                            type="button"
                            className="sit-btn sit-btn--secondary eSchoolEventEditForm__venueBtn"
                            onClick={this.createVenueFormOpen}
                          >
                            Create venue
                          </button>
                        </div>

                        {touched.venue && errors.venue ? (
                          <div className="alert alert-danger mt-2">{String(errors.venue)}</div>
                        ) : null}
                      </div>
                    </div>

                    {propz.get(values.venue, ['venueType']) !== 'TBD' && typeof point !== 'undefined' && (
                      <div className="eSchoolEventEditForm__mapArea">
                        <button
                          type="button"
                          className="eSchoolEventEditForm__mapToggle"
                          onClick={() => this.setState(({ isMapExpanded }) => ({ isMapExpanded: !isMapExpanded }))}
                        >
                          {this.state.isMapExpanded ? 'Hide map' : 'Check location on map'}
                        </button>
                        {this.state.isMapExpanded && (
                          <div className="eSchoolEventEditForm__mapWrap">
                            <Map
                              point={point}
                              getNewPoint={pointNext => {
                                this.getNewPoint(pointNext);
                                this.updateReasonVisibility(values);
                              }}
                              customStylingClass="eSchoolEventVenueMap"
                              isMarkerDraggable={true}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="eSchoolEventEditForm__grid eSchoolEventEditForm__gridSingle">
                      <div className="eSchoolEventEditForm__field eSchoolEventEditForm__fieldMedium">
                        <label className="eSchoolEventEditForm__label">Reply-to email address</label>
                        <Field
                          type="email"
                          name="replyToEmail"
                          className="form-control"
                          placeholder="Enter reply-to email address"
                          value={values.replyToEmail || ''}
                        />
                        {touched.replyToEmail && errors.replyToEmail ? (
                          <div className="alert alert-danger mt-2">{errors.replyToEmail}</div>
                        ) : null}
                      </div>
                    </div>
                  </section>
                )}

                {isNonClubEvent && (
                  <section className="eSchoolEventEditForm__section">
                    <div className="eSchoolEventEditForm__sectionHeader">
                      <h3 className="eSchoolEventEditForm__sectionTitle">Challenge</h3>
                      <div className="eSchoolEventEditForm__sectionText">
                        Turn this on if this event should use challenge settings.
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__challengeToggleOnly">
                      <div className="eSchoolEventEditForm__toggleCard">
                        <div className="eSchoolEventEditForm__toggleText">
                          <div className="eSchoolEventEditForm__toggleTitle">This is a challenge</div>
                          <div className="eSchoolEventEditForm__toggleHint">
                            Enable this to show challenge-specific options such as picture, leaderboard positions and
                            student access.
                          </div>
                        </div>

                        <Switch
                          name="isChallenge"
                          text=""
                          value={isChallenge}
                          customClass="mb-0"
                          onChange={this.handleChallengeSwitchChange}
                        />
                      </div>
                    </div>
                  </section>
                )}

                {showChallengeBlock && (
                  <section className="eSchoolEventEditForm__section">
                    <div className="eSchoolEventEditForm__sectionHeader">
                      <h3 className="eSchoolEventEditForm__sectionTitle">Challenge settings</h3>
                      <div className="eSchoolEventEditForm__sectionText">
                        Use this section for challenge-style individual events.
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__challengeCard">
                      <div className="eSchoolEventEditForm__challengeTopRow">
                        <div className="eSchoolEventEditForm__challengeImageBlock">
                          <label className="eSchoolEventEditForm__label">Event picture</label>

                          <input
                            type="file"
                            name="picUrl"
                            className="form-control-file"
                            onChange={event => this.onImageSelected(event, setFieldValue)}
                          />

                          {this.state.isImageError && (
                            <div className="alert alert-danger mt-2">Image upload failed. Please try again.</div>
                          )}

                          {values.picUrl && (
                            <div className="eSchoolEventEditForm__imagePreview">
                              <button
                                type="button"
                                className="eSchoolEventEditForm__imageRemove"
                                onClick={() => this.onRemoveImageClick(setFieldValue)}
                              >
                                ×
                              </button>
                              <img className="img-fluid img-thumbnail" src={values.picUrl} alt="Event picture" />
                            </div>
                          )}
                        </div>

                        <div className="eSchoolEventEditForm__challengeMetaBlock">
                          <label className="eSchoolEventEditForm__label">Number of leaderboard positions</label>
                          <Field type="text" name="leaguePositionsCount" className="form-control" />
                          {touched.leaguePositionsCount && errors.leaguePositionsCount ? (
                            <div className="alert alert-danger mt-2">{errors.leaguePositionsCount}</div>
                          ) : null}
                        </div>
                      </div>

                      <div className="eSchoolEventEditForm__togglesRow">
                        <div className="eSchoolEventEditForm__toggleCard">
                          <div className="eSchoolEventEditForm__toggleText">
                            <div className="eSchoolEventEditForm__toggleTitle">Students can submit results</div>
                            <div className="eSchoolEventEditForm__toggleHint">
                              Allow students to enter their own results where the activity supports it.
                            </div>
                          </div>

                          <Field name="isStudentCanSubmitResult">
                            {({ field }) => (
                              <Switch
                                {...field}
                                value={values.isStudentCanSubmitResult}
                                customClass="mb-0"
                                onChange={event => {
                                  const isStudentCanSubmitResult = event.target.checked;
                                  setFieldValue('isStudentCanSubmitResult', isStudentCanSubmitResult);
                                }}
                              />
                            )}
                          </Field>
                        </div>

                        <div className="eSchoolEventEditForm__toggleCard">
                          <div className="eSchoolEventEditForm__toggleText">
                            <div className="eSchoolEventEditForm__toggleTitle">Students can join</div>
                            <div className="eSchoolEventEditForm__toggleHint">
                              Allow students to join the challenge directly where appropriate.
                            </div>
                          </div>

                          <Field name="isStudentCanJoin">
                            {({ field }) => (
                              <Switch
                                {...field}
                                value={values.isStudentCanJoin}
                                customClass="mb-0"
                                onChange={event => {
                                  const isStudentCanJoin = event.target.checked;
                                  setFieldValue('isStudentCanJoin', isStudentCanJoin);
                                }}
                              />
                            )}
                          </Field>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {showReasonBlock && (
                  <section className="eSchoolEventEditForm__section">
                    <div className="eSchoolEventEditForm__sectionHeader">
                      <h3 className="eSchoolEventEditForm__sectionTitle">Reason for change</h3>
                      <div className="eSchoolEventEditForm__sectionText">
                        Optional. This can be included in notifications if event timing or venue has changed.
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__noticeCard">
                      <textarea
                        id="reasonForChange"
                        value={this.state.reasonForChange || ''}
                        placeholder="Add a short explanation for recipients, for example venue change, revised timings or weather-related update."
                        onChange={e => this.setState({ reasonForChange: e.target.value })}
                        className="form-control eSchoolEventEditForm__reasonTextarea"
                      />
                    </div>
                  </section>
                )}

                {isChangeFollowingEvents && (
                  <section className="eSchoolEventEditForm__section">
                    <div className="eSchoolEventEditForm__sectionHeader">
                      <h3 className="eSchoolEventEditForm__sectionTitle">Apply changes</h3>
                      <div className="eSchoolEventEditForm__sectionText">
                        Choose whether the update should affect only this event or the rest of the series.
                      </div>
                    </div>

                    <div className="eSchoolEventEditForm__noticeCard">
                      <SchoolEventClubEventRadioButtons onEditModeClick={this.onEditModeClick} editMode={editMode} />
                    </div>
                  </section>
                )}

                <div className="eSchoolEventEditForm__actions">
                  <div className="eSchoolEventEditForm__actionsButtons">
                    <Button
                      onClick={onCancel}
                      text="Cancel"
                      customClass="sit-btn sit-btn--secondary eSchoolEventEditForm__btnSecondary"
                    />

                    <button type="submit" className="sit-btn sit-btn--primary eSchoolEventEditForm__btnPrimary">
                      Save changes
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }
}
